import React from 'react';
import Image from 'next/image';
import Button from '@mui/material/Button';
import { Input } from '@/components/form';
import SubscribeModal from './SubscribeModal';
import JobTitleSearch, {
  JobOptionType,
} from '@/components/job-title/search-area/JobTitleSearch';
import { CityOption } from '@/context/city';
import { JOB_TITLE_PAGE_SUBSCRIBE_CITY_SEARCH_ID } from '@/constants/component-id';
import validators from '@/utils/validators';
import { useDebounce } from '@/utils/hooks';
import FormValidator from '@/utils/form-validator';
import { trackClickEvent } from '@/utils/site-track';
import subscribeIcon from '@/assets/job-title/subscribe-icon.svg';

type Props = {
  isJobTitle?: boolean;
  preferenceValue?: JobOptionType | null;
  dataEleId?: string;
  dataObserverId?: string;
  location?: CityOption | null;
};

const INPUT_TYPE_JOB_ID = 'job_id';
export const INPUT_TYPE_EMAIL = 'email';

const VALIDATOR_MAP = {
  [INPUT_TYPE_JOB_ID]: [validators.valueRequired('请选择需要订阅的岗位或公司')],
  [INPUT_TYPE_EMAIL]: [
    validators.valueRequired('请输入你的邮箱'),
    validators.email(),
  ],
};

const INPUT_TYPE_LIST = Object.keys(VALIDATOR_MAP);

type Field = (typeof INPUT_TYPE_LIST)[number];
type FieldType = Record<Field, string>;

const formValidator = new FormValidator(VALIDATOR_MAP);

const SubscribeJob = (props: Props) => {
  const {
    isJobTitle = true,
    preferenceValue,
    dataEleId = 'subscribe_job-title',
    dataObserverId = 'subscribe_job-title',
    location,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [jobValue, setJobValue] = React.useState<JobOptionType>({
    id: '',
    name: '',
  });
  const [cityValue, setCityValue] = React.useState<CityOption | undefined>({
    id: '',
    name: '',
    pinyin: '',
  });
  const [error, setError] = React.useState<FieldType>({});

  const handleChange = (val: JobOptionType) => {
    setJobValue(val);
    handleValidate({
      [INPUT_TYPE_JOB_ID]: val.id,
    });
  };

  const handleCityChange = (val: CityOption | undefined) => setCityValue(val);

  const handleEmailValidate = useDebounce((value) =>
    handleValidate({
      [INPUT_TYPE_EMAIL]: value,
    }),
  );

  const handleEmailChange = (val: string) => {
    setEmail(val);
    handleEmailValidate(null, val);
  };

  const handleError = (errorMsgs: FieldType) => {
    setError((prev) => ({
      ...prev,
      ...errorMsgs,
    }));
  };

  const handleValidate = (values: FieldType) => {
    formValidator.validate(values);
    const error = formValidator.getError();
    error && handleError(error as FieldType);
    return formValidator.isOk();
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const isOk = handleValidate({
      [INPUT_TYPE_JOB_ID]: jobValue.id,
      [INPUT_TYPE_EMAIL]: email,
    });
    if (isOk) {
      trackClickEvent(e);
      setOpen(true);
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <div
        className={`bg-white rounded-lg p-5${isJobTitle ? ' mt-6' : ''}`}
        data-observer-id={dataObserverId}
      >
        <div
          className={
            isJobTitle
              ? 'flex gap-4 items-center'
              : 'flex flex-col gap-2 items-center'
          }
        >
          <Image
            alt=""
            src={subscribeIcon}
            className={isJobTitle ? '' : 'mx-auto'}
          />
          <div>
            <p
              className={`font-semibold text-black/85 ${isJobTitle ? 'text-2xl' : 'text-xl'}`}
            >
              20,861+ 岗位更新等你来订阅
            </p>
            <p className="text-sm leading-5.5 mt-2">
              一键订阅最新的岗位，每周送达
            </p>
          </div>
        </div>
        <div className={isJobTitle ? 'mt-5' : 'mt-8'}>
          <div className={success ? 'block' : 'hidden'}>
            <div
              className={`flex flex-col${isJobTitle ? '' : ' items-center'}`}
            >
              <p
                className={`my-5 font-semibold text-2xl ${isJobTitle ? 'text-left' : 'text-center'}`}
              >
                🎉恭喜你，订阅成功
              </p>
              <span
                className="underline text-web-blue-500 cursor-pointer"
                onClick={() => setSuccess(false)}
              >
                继续订阅
              </span>
            </div>
          </div>
          <div className={success ? 'hidden' : 'block'}>
            <div
              className={
                isJobTitle
                  ? 'grid grid-cols-1 gap-4 tablet:flex'
                  : 'grid grid-cols-1 gap-4'
              }
            >
              <JobTitleSearch
                jobValue={jobValue}
                cityValue={cityValue}
                handleChange={handleChange}
                handleCityChange={handleCityChange}
                pageUniqueId={JOB_TITLE_PAGE_SUBSCRIBE_CITY_SEARCH_ID}
                searchError={error[INPUT_TYPE_JOB_ID]}
                preferenceValue={preferenceValue}
                location={location}
                multiSearch={true}
                searchType="job_search"
              />
              <Input
                value={email}
                onChange={handleEmailChange}
                error={!!error[INPUT_TYPE_EMAIL]}
                helperText={error[INPUT_TYPE_EMAIL]}
                placeholder="输入你的邮箱地址"
                className="flex-1"
                type={INPUT_TYPE_EMAIL}
              />
              <div
                className={`flex-1 flex ${isJobTitle ? 'flex-col' : 'flex-col-reverse'}`}
              >
                <Button
                  variant="contained"
                  fullWidth
                  className="!rounded-lg !h-12"
                  onClick={handleClick}
                  data-ele-id={dataEleId}
                  data-ele-group="subscribe"
                >
                  订阅
                </Button>
                <p className="text-xs text-black/65 my-2 line-clamp-1">
                  您可以在邮箱中随时取消订阅
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SubscribeModal
        open={open}
        email={email}
        city={cityValue}
        jobValue={jobValue}
        handleSuccess={handleSuccess}
        handleClose={handleClose}
      />
    </>
  );
};

export default SubscribeJob;
